import React from 'react';
import { CreditCard, LoaderCircle } from 'lucide-react';

import { Button } from '@/component-library/primitives/Button/Button';

interface StripeOAuthButtonProps {
  onSuccess?: (data: { stripeAccountId: string }) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
}

export const StripeOAuthButton: React.FC<StripeOAuthButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
  className = '',
}) => {
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const handleConnect = () => {
    const stripeClientId = process.env.REACT_APP_STRIPE_CLIENT_ID;
    let redirectUri = process.env.REACT_APP_STRIPE_REDIRECT_URI;

    if (!stripeClientId) {
      onError?.('Stripe Client ID is not configured. Please set REACT_APP_STRIPE_CLIENT_ID in your .env file.');
      return;
    }

    // Auto-detect redirect URI from current location if not set (for development/testing)
    if (!redirectUri) {
      const currentOrigin = window.location.origin;
      // Use the route that matches what was originally configured in Stripe Connect app
      redirectUri = `${currentOrigin}/onboarding/stripe-callback`;
      console.warn('REACT_APP_STRIPE_REDIRECT_URI not set. Using auto-detected URI:', redirectUri);
    }

    setIsRedirecting(true);

    // Build Stripe OAuth URL
    const stripeAuthUrl = new URL('https://connect.stripe.com/oauth/authorize');
    stripeAuthUrl.searchParams.append('response_type', 'code');
    stripeAuthUrl.searchParams.append('client_id', stripeClientId);
    stripeAuthUrl.searchParams.append('scope', 'read_write');
    stripeAuthUrl.searchParams.append('redirect_uri', redirectUri);

    // Redirect to Stripe
    window.location.href = stripeAuthUrl.toString();
  };

  return (
    <Button onClick={handleConnect} disabled={disabled || isRedirecting} size="large" className={`w-full ${className}`}>
      {isRedirecting ? (
        <>
          <LoaderCircle size={20} className="animate-spin" />
          <span>Redirecting to Stripe...</span>
        </>
      ) : (
        <>
          <CreditCard size={20} />
          <span>Connect with Stripe</span>
        </>
      )}
    </Button>
  );
};
